/// The appearance settings a project chooses: two skins, the composer's corner
/// as its own axis, and bubbles as a shape and a fill. Every value here ships;
/// the stored one is what a project gets before anyone opens the console.

export type MessengerSkin = 'quiet' | 'standard';
export type ComposerShape = 'round' | 'square';
export type BubbleShape = 'rounded' | 'tailed';
export type BubbleFill = 'filled' | 'outlined';
export type LauncherShape = 'circle' | 'pill' | 'squircle';
export type LauncherFill = 'filled' | 'outlined';
export type LauncherContent = 'avatar' | 'glyph' | 'team_faces';
export type LauncherSize = 'large' | 'medium' | 'small';
export type NamePlacement = 'above' | 'hidden' | 'inline';
export type StampPlacement = 'beside_name' | 'none' | 'under_turn';
export type StampContent = 'time' | 'time_and_latency';

export interface AppearanceSettings {
  bubbleFill: BubbleFill;
  bubbleShape: BubbleShape;
  composerShape: ComposerShape;
  dayDividers: boolean;
  launcherContent: LauncherContent;
  launcherFill: LauncherFill;
  launcherShape: LauncherShape;
  launcherSize: LauncherSize;
  nameAvatar: boolean;
  namePlacement: NamePlacement;
  skin: MessengerSkin;
  stampContent: StampContent;
  stampPlacement: StampPlacement;
}

export const DEFAULT_APPEARANCE_SETTINGS: AppearanceSettings = {
  bubbleFill: 'filled',
  bubbleShape: 'tailed',
  composerShape: 'round',
  dayDividers: true,
  launcherContent: 'glyph',
  launcherFill: 'outlined',
  launcherShape: 'circle',
  launcherSize: 'small',
  nameAvatar: true,
  namePlacement: 'hidden',
  skin: 'standard',
  stampContent: 'time',
  stampPlacement: 'none',
};

const SKINS: readonly string[] = ['quiet', 'standard'];
const COMPOSER_SHAPES: readonly string[] = ['round', 'square'];
const BUBBLE_SHAPES: readonly string[] = ['rounded', 'tailed'];
const BUBBLE_FILLS: readonly string[] = ['filled', 'outlined'];
const LAUNCHER_SHAPES: readonly string[] = ['circle', 'pill', 'squircle'];
const LAUNCHER_FILLS: readonly string[] = ['filled', 'outlined'];
const LAUNCHER_CONTENTS: readonly string[] = ['avatar', 'glyph', 'team_faces'];
const LAUNCHER_SIZES: readonly string[] = ['large', 'medium', 'small'];
const NAME_PLACEMENTS: readonly string[] = ['above', 'hidden', 'inline'];
const STAMP_PLACEMENTS: readonly string[] = ['beside_name', 'none', 'under_turn'];
const STAMP_CONTENTS: readonly string[] = ['time', 'time_and_latency'];

const pick = <T extends string>(value: unknown, allowed: readonly string[], fallback: T): T => (typeof value === 'string' && allowed.includes(value) ? (value as T) : fallback);

const pickBoolean = (value: unknown, fallback: boolean): boolean => (typeof value === 'boolean' ? value : fallback);

const LEGACY_LAUNCHER: Record<string, [LauncherShape, LauncherFill, LauncherContent, LauncherSize]> = {
  avatar_circle: ['circle', 'outlined', 'avatar', 'large'],
  circle_filled: ['circle', 'filled', 'glyph', 'medium'],
  circle_outlined: ['circle', 'outlined', 'glyph', 'small'],
  pill_labelled: ['pill', 'filled', 'glyph', 'medium'],
  pill_team: ['pill', 'outlined', 'team_faces', 'medium'],
  squircle_filled: ['squircle', 'filled', 'glyph', 'medium'],
};

const LEGACY_NAME: Record<string, [NamePlacement, boolean]> = {
  above: ['above', false],
  avatar_only: ['hidden', true],
  gutter: ['above', true],
  hidden: ['hidden', false],
  inline: ['inline', false],
};

const LEGACY_STAMP: Record<string, [boolean, StampPlacement, StampContent]> = {
  cluster: [true, 'none', 'time'],
  latency: [false, 'beside_name', 'time_and_latency'],
  none: [false, 'none', 'time'],
  trailing: [false, 'under_turn', 'time'],
  with_name: [false, 'beside_name', 'time'],
};

/// The three skins of 2026-08-26 were one look and one composer each. The
/// composer is its own axis now, so a skin a visitor's browser cached before
/// migration 0066 is read as the pair it used to imply.
const LEGACY_SKIN: Record<string, [MessengerSkin, ComposerShape]> = {
  concierge: ['standard', 'round'],
  console: ['standard', 'square'],
  quiet: ['quiet', 'square'],
};

/// `outlined` had equal corners and no fill; `hairline` drew no bubble at all
/// and lands on the nearest thing that still does.
const LEGACY_BUBBLE: Record<string, [BubbleShape, BubbleFill]> = {
  hairline: ['rounded', 'outlined'],
  outlined: ['rounded', 'outlined'],
  rounded: ['rounded', 'filled'],
  tailed: ['tailed', 'filled'],
};

/// One rule, the same one the database refuses with a check constraint, so a
/// browser should never meet a pair that breaks it. It still repairs rather
/// than throws: this runs inside a customer's page, and a widget that renders
/// nothing because two settings disagree is worse than one that moves the clock
/// somewhere it can actually be drawn.
export function resolveAppearance(config: unknown): AppearanceSettings {
  const source = typeof config === 'object' && config !== null ? (config as Record<string, unknown>) : {};

  const rawLauncherStyle = typeof source.launcherStyle === 'string' ? source.launcherStyle : undefined;
  const legacyLauncher = rawLauncherStyle === undefined ? undefined : LEGACY_LAUNCHER[rawLauncherStyle];

  const rawNamePlacement = typeof source.namePlacement === 'string' ? source.namePlacement : undefined;
  const legacyName = rawNamePlacement === undefined ? undefined : LEGACY_NAME[rawNamePlacement];

  const rawTimestampStyle = typeof source.timestampStyle === 'string' ? source.timestampStyle : undefined;
  const legacyStamp = rawTimestampStyle === undefined ? undefined : LEGACY_STAMP[rawTimestampStyle];

  const rawSkin = typeof source.skin === 'string' ? source.skin : undefined;
  const legacySkin = rawSkin === undefined ? undefined : LEGACY_SKIN[rawSkin];

  const rawBubbleStyle = typeof source.bubbleStyle === 'string' ? source.bubbleStyle : undefined;
  const legacyBubble = rawBubbleStyle === undefined ? undefined : LEGACY_BUBBLE[rawBubbleStyle];

  const settings: AppearanceSettings = {
    bubbleFill: pick(source.bubbleFill, BUBBLE_FILLS, legacyBubble?.[1] ?? DEFAULT_APPEARANCE_SETTINGS.bubbleFill),
    bubbleShape: pick(source.bubbleShape, BUBBLE_SHAPES, legacyBubble?.[0] ?? DEFAULT_APPEARANCE_SETTINGS.bubbleShape),
    composerShape: pick(source.composerShape, COMPOSER_SHAPES, legacySkin?.[1] ?? DEFAULT_APPEARANCE_SETTINGS.composerShape),
    dayDividers: pickBoolean(source.dayDividers, legacyStamp?.[0] ?? DEFAULT_APPEARANCE_SETTINGS.dayDividers),
    launcherContent: pick(source.launcherContent, LAUNCHER_CONTENTS, legacyLauncher?.[2] ?? DEFAULT_APPEARANCE_SETTINGS.launcherContent),
    launcherFill: pick(source.launcherFill, LAUNCHER_FILLS, legacyLauncher?.[1] ?? DEFAULT_APPEARANCE_SETTINGS.launcherFill),
    launcherShape: pick(source.launcherShape, LAUNCHER_SHAPES, legacyLauncher?.[0] ?? DEFAULT_APPEARANCE_SETTINGS.launcherShape),
    launcherSize: pick(source.launcherSize, LAUNCHER_SIZES, legacyLauncher?.[3] ?? DEFAULT_APPEARANCE_SETTINGS.launcherSize),
    nameAvatar: pickBoolean(source.nameAvatar, legacyName?.[1] ?? DEFAULT_APPEARANCE_SETTINGS.nameAvatar),
    namePlacement: pick(source.namePlacement, NAME_PLACEMENTS, legacyName?.[0] ?? DEFAULT_APPEARANCE_SETTINGS.namePlacement),
    skin: pick(source.skin, SKINS, legacySkin?.[0] ?? DEFAULT_APPEARANCE_SETTINGS.skin),
    stampContent: pick(source.stampContent, STAMP_CONTENTS, legacyStamp?.[2] ?? DEFAULT_APPEARANCE_SETTINGS.stampContent),
    stampPlacement: pick(source.stampPlacement, STAMP_PLACEMENTS, legacyStamp?.[1] ?? DEFAULT_APPEARANCE_SETTINGS.stampPlacement),
  };

  if (settings.stampPlacement === 'beside_name' && settings.namePlacement === 'hidden') {
    settings.stampPlacement = 'under_turn';
  }
  return settings;
}
